import {
  getSPDataWithParams,
  postSPData,
  postSPDataWithoutParams,
  putSPDataWithoutParams,
} from './services'

/**
 * Get overall roster data for all or selected clinics
 * @param {boolean} debug - If true, adds debug information to response showing filtering and scope details
 */
export async function getOverallRosterData({
  fromDate,
  toDate,
  clinicBrandIds = null,
  clinicLocationIds = null,
  chamberRoomIds = null,
  specializationIds = null, // Legacy parameter - kept for backward compatibility
  targetSpecialitySlugs = null, // New parameter - preferred over specializationIds
  eventTypes = null,
  includeUnoccupied = true,
  includeBookedSlots = true,
  debug = false,
}) {
  const params = {
    fromDate,
    toDate,
    includeUnoccupied,
    includeBookedSlots,
  }

  // Only add debug parameter if explicitly requested AND not in production
  // This ensures debug is never sent in production builds (double-check for safety)
  const isProduction = import.meta.env.PROD || import.meta.env.MODE === 'production'
  if (debug && !isProduction) {
    params.debug = true
  }

  if (clinicBrandIds && clinicBrandIds.length > 0) {
    params.clinicBrandIds = clinicBrandIds.join(',')
  }

  if (clinicLocationIds && clinicLocationIds.length > 0) {
    params.clinicLocationIds = clinicLocationIds.join(',')
  }

  if (chamberRoomIds && chamberRoomIds.length > 0) {
    params.chamberRoomIds = chamberRoomIds.join(',')
  }

  // Prefer targetSpecialitySlugs over specializationIds (legacy)
  if (targetSpecialitySlugs && targetSpecialitySlugs.length > 0) {
    params.targetSpecialitySlugs = Array.isArray(targetSpecialitySlugs) ? targetSpecialitySlugs.join(',') : targetSpecialitySlugs
  } else if (specializationIds && specializationIds.length > 0) {
    // Fallback to legacy specializationIds for backward compatibility
    params.specializationIds = Array.isArray(specializationIds) ? specializationIds.join(',') : specializationIds
  }

  if (eventTypes && eventTypes.length > 0) {
    params.eventTypes = eventTypes.join(',')
  }

  const resp = await getSPDataWithParams(
    '/api/secure/cc/support/roster-manager/overall-roster',
    params,
  )

  return resp
}

/**
 * Get roster summary statistics
 */
export async function getRosterSummary({
  fromDate,
  toDate,
  clinicBrandIds = null,
  clinicLocationIds = null,
  chamberRoomIds = null,
  specializationIds = null, // Legacy parameter - kept for backward compatibility
  targetSpecialitySlugs = null, // New parameter - preferred over specializationIds
  eventTypes = null,
  groupBy = 'clinic',
}) {
  const params = {
    fromDate,
    toDate,
    groupBy,
  }

  if (clinicBrandIds && clinicBrandIds.length > 0) {
    params.clinicBrandIds = clinicBrandIds.join(',')
  }

  if (clinicLocationIds && clinicLocationIds.length > 0) {
    params.clinicLocationIds = clinicLocationIds.join(',')
  }

  if (chamberRoomIds && chamberRoomIds.length > 0) {
    params.chamberRoomIds = chamberRoomIds.join(',')
  }

  // Prefer targetSpecialitySlugs over specializationIds (legacy)
  if (targetSpecialitySlugs && targetSpecialitySlugs.length > 0) {
    params.targetSpecialitySlugs = Array.isArray(targetSpecialitySlugs) ? targetSpecialitySlugs.join(',') : targetSpecialitySlugs
  } else if (specializationIds && specializationIds.length > 0) {
    // Fallback to legacy specializationIds for backward compatibility
    params.specializationIds = Array.isArray(specializationIds) ? specializationIds.join(',') : specializationIds
  }

  if (eventTypes && eventTypes.length > 0) {
    params.eventTypes = eventTypes.join(',')
  }

  const resp = await getSPDataWithParams(
    '/api/secure/cc/support/roster-manager/roster-summary',
    params,
  )

  return resp
}

/**
 * Get practitioner roster (Support Portal)
 * Uses /api/secure/cc/support/teacher/roster endpoint
 * Support staff can fetch any mentor roster by mentorId
 */
export async function getPractitionerRoster({ fromDate, toDate, mentorId, includeBookedSlots = false }) {
  const params = {
    fromDate,
    toDate,
  }

  if (mentorId) {
    params.mentorId = mentorId
  }

  if (includeBookedSlots !== undefined) {
    params.includeBookedSlots = includeBookedSlots
  }

  const resp = await getSPDataWithParams('/api/secure/cc/support/teacher/roster', params)

  return resp
}

// Backward-compatible alias used by some older roster hooks/views.
export const getMyRoster = getPractitionerRoster

export async function forceCheckoutActivePractitioner({ practitionerId, reason = null, targetRole = null }) {
  const payload = {
    practitionerId,
  }

  if (reason) {
    payload.reason = reason
  }

  if (targetRole) {
    payload.targetRole = targetRole
  }

  return postSPDataWithoutParams(
    '/api/secure/cc/support/chamber-rooms/checkin/force-checkout/practitioner',
    payload,
  )
}

/**
 * Bulk create roster slots
 */
export async function bulkCreateRosterSlots(slots) {
  const resp = await postSPData('/api/secure/cc/support/roster-manager/roster-slots/bulk', {
    slots,
  })

  return resp
}

/**
 * Get allowed clinics for roster manager
 */
export async function getAllowedClinicsForManager() {
  const resp = await getSPDataWithParams(
    '/api/secure/cc/support/roster-manager/allowed-clinics',
  )

  return resp
}

/**
 * Get chamber rooms with clinic brand, location, and schedule details
 * Returns only rostered rooms with full details
 * 
 * @param {string[]|string|null} clinicBrandIds - Comma-separated or array of clinic brand IDs
 * @param {string[]|string|null} clinicLocationIds - Comma-separated or array of clinic location IDs
 * @param {string[]|string|null} chamberRoomIds - Comma-separated or array of chamber room IDs
 * @param {string[]|string|null} preferredEventTypes - Comma-separated or array of event types
 * @param {string[]|string|null} targetSpecialitySlugs - Comma-separated or array of target speciality slugs (for filtering rooms by roster schedule target specialities)
 * @param {boolean} rostered - Only return rostered rooms (default: true)
 * @param {boolean} includeAssociatedMentors - Include mentors from mentorsAndAuditors, filtered by RosterSchedule.eventTypes (default: false)
 * @returns {Promise} API response with chamber-room array containing nested clinicBrand, clinicLocation, clinicSchedule, rosterSchedule, and optionally associatedMentors
 */
/**
 * Get EMR usage logs for a mentor (check-in/check-out presence logs)
 * Used to compare expected roster presence vs actual presence
 * 
 * @param {string} fromDate - Start date (YYYY-MM-DD)
 * @param {string} toDate - End date (YYYY-MM-DD)
 * @param {string} mentorId - Mentor/practitioner ID
 * @param {string[]|string|null} clinicBrandIds - Optional filter by clinic brands
 * @param {string[]|string|null} clinicLocationIds - Optional filter by clinic locations
 * @param {string[]|string|null} chamberRoomIds - Optional filter by chamber rooms
 * @param {string[]|string|null} eventTypes - Optional filter by event types
 * @param {string[]|string|null} logEventIds - Optional filter by log event types (log-check-in, log-check-out, etc.)
 * @returns {Promise} API response with usageLogs array and summary
 */
export async function getMentorEmrUsageLogs({
  fromDate,
  toDate,
  mentorId,
  clinicBrandIds = null,
  clinicLocationIds = null,
  chamberRoomIds = null,
  eventTypes = null,
  logEventIds = null,
}) {
  const params = {
    fromDate,
    toDate,
    mentorId,
  }

  if (clinicBrandIds && clinicBrandIds.length > 0) {
    params.clinicBrandIds = Array.isArray(clinicBrandIds) ? clinicBrandIds.join(',') : clinicBrandIds
  }

  if (clinicLocationIds && clinicLocationIds.length > 0) {
    params.clinicLocationIds = Array.isArray(clinicLocationIds) ? clinicLocationIds.join(',') : clinicLocationIds
  }

  if (chamberRoomIds && chamberRoomIds.length > 0) {
    params.chamberRoomIds = Array.isArray(chamberRoomIds) ? chamberRoomIds.join(',') : chamberRoomIds
  }

  if (eventTypes && eventTypes.length > 0) {
    params.eventTypes = Array.isArray(eventTypes) ? eventTypes.join(',') : eventTypes
  }

  if (logEventIds && logEventIds.length > 0) {
    params.logEventIds = Array.isArray(logEventIds) ? logEventIds.join(',') : logEventIds
  }

  const resp = await getSPDataWithParams('/api/secure/cc/support/mentor/emr-usage-logs', params)

  return resp
}

export async function getChamberRoomsWithDetails({
  clinicBrandIds = null,
  clinicLocationIds = null,
  chamberRoomIds = null,
  preferredEventTypes = null,
  targetSpecialitySlugs = null,
  rostered = true,
  includeAssociatedMentors = false,
}) {
  const params = {}

  if (clinicBrandIds && clinicBrandIds.length > 0) {
    params.clinicBrandIds = Array.isArray(clinicBrandIds) ? clinicBrandIds.join(',') : clinicBrandIds
  }

  if (clinicLocationIds && clinicLocationIds.length > 0) {
    params.clinicLocationIds = Array.isArray(clinicLocationIds) ? clinicLocationIds.join(',') : clinicLocationIds
  }

  if (chamberRoomIds && chamberRoomIds.length > 0) {
    params.chamberRoomIds = Array.isArray(chamberRoomIds) ? chamberRoomIds.join(',') : chamberRoomIds
  }

  if (preferredEventTypes && preferredEventTypes.length > 0) {
    params.preferredEventTypes = Array.isArray(preferredEventTypes) ? preferredEventTypes.join(',') : preferredEventTypes
  }

  if (targetSpecialitySlugs && targetSpecialitySlugs.length > 0) {
    // Filter rooms whose roster schedules target these specialities
    // Empty/missing means all target specialities allowed
    params.targetSpecialitySlugs = Array.isArray(targetSpecialitySlugs) ? targetSpecialitySlugs.join(',') : targetSpecialitySlugs
  }

  // Include associated mentors if requested
  // Mentors are already filtered by RosterSchedule.eventTypes on the server
  if (includeAssociatedMentors) {
    params.includeAssociatedMentors = 'true'
  }

  // Explicitly set rostered parameter - always include it to ensure API filters correctly
  // API expects rostered=true to filter only rostered rooms
  // Convert boolean to string for query parameter to ensure proper URL encoding
  params.rostered = String(rostered !== undefined ? rostered : true)

  const resp = await getSPDataWithParams(
    '/api/secure/cc/support/roster-manager/chamber-rooms',
    params,
  )

  return resp
}

export async function createRosterSchedule(payload) {
  return postSPDataWithoutParams(
    '/api/secure/cc/support/roster-manager/roster-schedules',
    payload,
  )
}

export async function updateRosterSchedule(chamberRoomId, payload) {
  return putSPDataWithoutParams(
    `/api/secure/cc/support/roster-manager/roster-schedules/${chamberRoomId}`,
    payload,
  )
}

export async function getEffectiveRosterSchedule(chamberRoomId) {
  return getSPDataWithParams('/api/secure/cc/support/roster-manager/roster-schedules/effective', {
    chamberRoomId,
  })
}
